import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import SearchableDropdown from "./SearchableDropdown";

const VISIT_TYPES = ["call", "F2F meeting", "product demo", "training", "Project Documentation", "Internal Discussion with Team", "Requirement Gathering from Client", "Installation", "Follow Up", "payment Collection", "New Customer", "Other"];
const CLIENT_TYPES = ["Prospect", "Existing Client", "AP", "CA", "Partner", "STP", "Government", "Other"];
const TRAVEL_MODES = ["Own Vehicle", "Public Transport", "Company Vehicle", "Auto/Cab", "Train", "Flight", "Walking"];

const EMPTY_FORM = {
    visit_type: "",
    client_type: "",
    visit_purpose: "",
    product: "",
    customer_name: "",
    address: "",
    state: "",
    pincode: "",
    contact_person: "",
    contact_number: "",
    gps_location: "",
    location_name: "",
    tally_serial_number: "",
    check_in: "",
    check_out: "",
    remarks: "",
    referred_by_contact_person: "",
    referred_person_number: "",
    travel_from: "",
    travel_to: "",
    mode_of_transpotation: "",
    killometer: "",
    expense_amount: "",
};
/* ── Toast ─────────────────────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
    useEffect(() => {
        const t = setTimeout(onClose, 5000);
        return () => clearTimeout(t);
    }, [onClose]);

    const styles = {
        success: { bg: "#059669", icon: "✓" },
        error: { bg: "#dc2626", icon: "✕" },
        info: { bg: "#0369a1", icon: "ℹ" },
        warning: { bg: "#d97706", icon: "⚠" },
    };
    const s = styles[type] || styles.info;

    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "11px 16px",
            borderRadius: 8,
            background: s.bg,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            animation: "drToastIn 0.3s ease",
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
        }}>
            <span style={{ fontSize: 15 }}>{s.icon}</span>
            {message}
            <button
                onClick={onClose}
                style={{
                    marginLeft: "auto", background: "none", border: "none",
                    color: "rgba(255,255,255,0.8)", cursor: "pointer", fontSize: 14,
                    padding: "0 0 0 8px", lineHeight: 1,
                }}
            >✕</button>
        </div>
    );
}

/* ── Visit Duration calculator ──────────────────────────────────────────────── */
function calcDuration(checkIn, checkOut) {
    if (!checkIn || !checkOut) return null;
    const [h1, m1] = checkIn.split(":").map(Number);
    const [h2, m2] = checkOut.split(":").map(Number);
    const diff = h2 * 60 + m2 - (h1 * 60 + m1);
    if (diff <= 0) return null;
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
}

/* ── Reverse geocode coords → readable place name ───────────────────────────── */
async function reverseGeocode(lat, lng) {
    try {
        const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
            {
                headers: {
                    "Accept-Language": "en",
                },
            }
        );

        const data = await res.json();

        if (data?.address) {
            const a = data.address;

            return [
                a.road,
                a.suburb || a.neighbourhood,
                a.city || a.town || a.village,
                a.state
            ].filter(Boolean).join(", ");
        }

        return data.display_name || `${lat},${lng}`;
    } catch {
        return `${lat.toFixed(5)},${lng.toFixed(5)}`;
    }
}

/* ── Shared styles ───────────────────────────────────────────────────────────── */
const inputStyle = {
    width: "100%",
    padding: "7px 11px",
    fontSize: 13,
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    outline: "none",
    background: "#fff",
    color: "#1e293b",
    transition: "border 0.15s",
    fontFamily: "Inter, sans-serif",
    boxSizing: "border-box",
};

const selectStyle = {
    ...inputStyle,
    appearance: "none",
    cursor: "pointer",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    paddingRight: 30,
};

function FormField({ label, required, children }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {label}{required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
            </label>
            {children}
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT — Props: open (bool), onClose (fn)
══════════════════════════════════════════════════════════════════════════════ */
export default function DailyReport({ open, onClose }) {
    const [showForm, setShowForm] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [records, setRecords] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    const [checkInLoading, setCheckInLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [productOptions, setProductOptions] = useState([]);
    const [toast, setToast] = useState(null);

    // Read employee_id from localStorage (set during login)
    const employeeData = JSON.parse(localStorage.getItem("employee") || "{}");
    const employeeId = employeeData?.employee_id || "";
    const employeeName = employeeData?.employee_name || "";

    const VISIT_TYPE_OPTIONS = VISIT_TYPES.map(item => ({
        label: item,
        value: item,
    }));

    const CLIENT_TYPE_OPTIONS = CLIENT_TYPES.map(item => ({
        label: item,
        value: item,
    }));

    const TRAVEL_MODE_OPTIONS = TRAVEL_MODES.map(item => ({
        label: item,
        value: item,
    }));

    const showToast = useCallback((message, type = "info") => setToast({ message, type }), []);

    const fetchProducts = useCallback(async () => {
        try {
            const res = await axios.get(
                `${SERVER_URL}/Rahul/fetchProductAndCategory.php`
            );

            if (res.data.success) {

                // ✅ FIX: correct key is "products", not "data"
                const filtered = res.data.products
                    .filter(item => item.status === "ACTIVE") // ✅ ONLY ACTIVE
                    .map(item => ({
                        label: item.name,   // <-- from PHP response
                        value: item.name,
                    }));

                setProductOptions(filtered);
            }

        } catch (error) {
            console.error("Product fetch error:", error);
            showToast("Failed to load products", "error");
        }
    }, [showToast]);

    const fetchRecords = useCallback(async () => {
        setTableLoading(true);
        try {
            const res = await axios.get(
                `${SERVER_URL}/Rahul/getDailyRoutine.php`,
                {
                    params: {
                        employee_id: employeeId   // ✅ send logged-in employee ID
                    }
                }
            );

            if (res.data.success) setRecords(res.data.data);
        } catch {
            showToast("Failed to fetch records", "error");
        } finally {
            setTableLoading(false);
        }
    }, [showToast, employeeId]);

    useEffect(() => {

        if (open) {

            fetchRecords();

            fetchProducts();
        }

    }, [open, fetchRecords, fetchProducts]);
    useEffect(() => { if (!open) setShowForm(false); }, [open]);

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") { showForm ? setShowForm(false) : onClose(); }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [showForm, onClose]);

    const openAddForm = () => { setEditRecord(null); setForm(EMPTY_FORM); setShowForm(true); };
    const openEditForm = (rec) => {
        setEditRecord(rec);
        setForm({
            ...rec,
            location_name: rec.location_name || rec.gps_location || "",
        });
        setShowForm(true);
    };
    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm(f => {
            if (name === "killometer") {
                const km = value === "" ? "" : Number(value);

                return {
                    ...f,
                    killometer: value,
                    expense_amount:
                        value !== "" && Number.isFinite(km) && km >= 0
                            ? (km * 3).toFixed(2)
                            : ""
                };
            }

            return {
                ...f,
                [name]: value
            };
        });
    };

    const handleTravelModeChange = (value) => {
        setForm(f => ({
            ...f,
            mode_of_transpotation: value,

            // Clear kilometer and expense when not using own vehicle
            killometer: value === "Own Vehicle" ? f.killometer : "",
            expense_amount: value === "Own Vehicle" ? f.expense_amount : ""
        }));
    };

    /* ── Check In: fetch GPS + reverse geocode, then stamp time ── */
    const handleCheckIn = async () => {
        if (form.check_in) {
            showToast("Already checked in.", "warning");
            return;
        }

        if (!navigator.geolocation) {
            showToast("Geolocation not supported.", "error");
            return;
        }

        setCheckInLoading(true);

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;

                const coords = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;
                const now = new Date().toTimeString().slice(0, 5);

                // ⚡ STEP 1: instantly set coords + checkin
                setForm(f => ({
                    ...f,
                    gps_location: coords,
                    location_name: coords, // temporary
                    check_in: now
                }));

                showToast(`Checked in at ${now}`, "success");
                setCheckInLoading(false);

                // ⚡ STEP 2: fetch readable location in background
                reverseGeocode(latitude, longitude).then(name => {
                    setForm(f => ({
                        ...f,
                        location_name: name,
                        gps_location: name // ✅ store readable name instead
                    }));
                });

            },
            (err) => {
                setCheckInLoading(false);
                showToast("Location fetch failed. Enable GPS.", "error");
            },
            {
                enableHighAccuracy: false, // ⚡ faster
                timeout: 5000,             // ⚡ reduce wait
                maximumAge: 60000          // ⚡ use cached location
            }
        );
    };
    /* ── Check Out: one-press only ── */
    const handleCheckOut = () => {
        if (!form.check_in) {
            showToast("Please Check In first before checking out.", "error");
            return;
        }
        if (form.check_out) {
            showToast("You have already checked out for this entry.", "warning");
            return;
        }
        const now = new Date().toTimeString().slice(0, 5);
        setForm(f => ({ ...f, check_out: now }));
        showToast(`✓ Checked out at ${now}`, "success");
    };

    const handleSubmit = async () => {
        if (!form.gps_location) {
            showToast("Location is required. Please use Check In to capture your location.", "error");
            return;
        }
        if (!form.visit_type) { showToast("Please select Visit Type.", "error"); return; }
        if (!form.customer_name) { showToast("Firm Name is required.", "error"); return; }
        if (!form.contact_person) { showToast("Contact Person is required.", "error"); return; }
        if (
            form.mode_of_transpotation === "Own Vehicle" &&
            (!form.killometer || Number(form.killometer) <= 0)
        ) {
            showToast("Please enter valid kilometers for Own Vehicle.", "error");
            return;
        }
        setLoading(true);
        try {
            const url = editRecord
                ? `${SERVER_URL}/Rahul/updateDailyRoutine.php`
                : `${SERVER_URL}/Rahul/addDailyRoutine.php`;

            const payload = {
                ...form,
                employee_id: employeeId,
                ...(editRecord ? { slNo: editRecord.slNo } : {}),
            };

            const res = await axios.post(url, payload);
            if (res.data.success) {
                showToast(editRecord ? "Record updated successfully!" : "Record added successfully!", "success");
                setShowForm(false);
                fetchRecords();
            } else {
                showToast(res.data.message || "Operation failed", "error");
            }
        } catch {
            showToast("Server error. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    const duration = calcDuration(form.check_in, form.check_out);

    /* ── TABLE COLUMNS ── */
    const cols = [
        { key: "slNo", label: "#" },
        { key: "routine_date", label: "Date" },
        { key: "visit_type", label: "Visit Type" },
        { key: "client_type", label: "Client Type" },
        { key: "visit_purpose", label: "Purpose" },
        { key: "product", label: "Product" },
        { key: "customer_name", label: "Firm Name" },
        { key: "contact_person", label: "Contact Person" },
        { key: "contact_number", label: "Contact No." },
        { key: "gps_location", label: "Location" },
        { key: "tally_serial_number", label: "Tally S/N" },
        { key: "_duration", label: "Duration" },
        { key: "remarks", label: "Remarks" },
        { key: "referred_by_contact_person", label: "Referred By" },
        { key: "_from_to", label: "From → To" },
        { key: "mode_of_transpotation", label: "Travel Mode" },
        { key: "killometer", label: "Kilometers (KM)" },
        { key: "expense_amount", label: "Expense (₹)" },
        { key: "_actions", label: "" },
    ];

    const Badge = ({ text, scheme }) => {
        const schemes = {
            blue: { bg: "#eff6ff", color: "#1e40af" },
            purple: { bg: "#f5f3ff", color: "#6d28d9" },
            green: { bg: "#f0fdf4", color: "#15803d" },
            amber: { bg: "#fefce8", color: "#a16207" },
        };
        const s = schemes[scheme] || schemes.blue;
        return (
            <span style={{
                background: s.bg, color: s.color,
                padding: "2px 8px", borderRadius: 6,
                fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
            }}>{text}</span>
        );
    };

    /* ════════════════════════════════ JSX ════════════════════════════════ */
    return (
        <>
            <style>{`
        @keyframes drFadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes drSlideUp { from { transform:translateY(24px);opacity:0 } to { transform:translateY(0);opacity:1 } }
        @keyframes drToastIn { from { transform:translateY(-12px);opacity:0 } to { transform:translateY(0);opacity:1 } }
        .dr-input:focus { border-color: #1e40af !important; box-shadow: 0 0 0 3px rgba(30,64,175,0.1) !important; }
        .dr-btn-edit:hover { background: #f1f5f9 !important; border-color: #cbd5e1 !important; }
        .dr-row:hover td { background: #f8fafc !important; }
        .dr-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .dr-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .dr-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .dr-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .dr-checkin-btn:disabled { opacity: 0.5; cursor: not-allowed !important; }
      `}</style>

            {/* ─── GLOBAL TOAST (above popup) ───────────────────────────────────── */}
            {toast && (
                <div style={{
                    position: "fixed",
                    top: 18,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 9999,
                    minWidth: 320,
                    maxWidth: 500,
                    animation: "drToastIn 0.3s ease",
                }}>
                    <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
                </div>
            )}

            {/* ─── BACKDROP ─────────────────────────────────────────────────────── */}
            <div
                onClick={(e) => { if (!showForm && e.target === e.currentTarget) onClose(); }}
                style={{
                    position: "fixed", inset: 0, zIndex: 9000,
                    background: "rgba(15,23,42,0.4)",
                    backdropFilter: "blur(2px)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 20,
                    animation: "drFadeIn 0.2s ease",
                    fontFamily: "Inter, sans-serif",
                }}
            >

                {/* ─── TABLE MODAL ──────────────────────────────────────────────── */}
                {!showForm && (
                    <div style={{
                        background: "#fff",
                        borderRadius: 16,
                        width: "100%", maxWidth: 1120,
                        maxHeight: "88vh",
                        display: "flex", flexDirection: "column",
                        boxShadow: "0 20px 60px rgba(15,23,42,0.16)",
                        border: "1px solid #e2e8f0",
                        animation: "drSlideUp 0.25s ease",
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: "15px 20px",
                            borderBottom: "1px solid #f1f5f9",
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            flexShrink: 0,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{ width: 4, height: 22, borderRadius: 4, background: "linear-gradient(180deg,#1e40af,#0369a1)" }} />
                                <div>
                                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1e293b" }}>Daily Visit Report</h2>
                                    <p style={{ margin: "1px 0 0", fontSize: 11, color: "#94a3b8" }}>
                                        {tableLoading ? "Loading…" : `${records.length} record${records.length !== 1 ? "s" : ""}`}
                                        {employeeId && (
                                            <span style={{ marginLeft: 8, color: "#1e40af", fontWeight: 600 }}>
                                                · {employeeName} ({employeeId})
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <button
                                    onClick={openAddForm}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 5,
                                        padding: "7px 14px", borderRadius: 8, border: "none",
                                        background: "linear-gradient(90deg,#1e40af,#0369a1)",
                                        color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
                                        boxShadow: "0 2px 8px rgba(30,64,175,0.2)",
                                    }}
                                >
                                    <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add Entry
                                </button>
                                <button
                                    onClick={onClose}
                                    style={{
                                        width: 32, height: 32, borderRadius: 8,
                                        border: "1px solid #e2e8f0", background: "#f8fafc",
                                        cursor: "pointer", display: "flex", alignItems: "center",
                                        justifyContent: "center", color: "#64748b", fontSize: 16,
                                    }}
                                >✕</button>
                            </div>
                        </div>

                        {/* Table area */}
                        <div className="dr-scrollbar" style={{ overflow: "auto", flex: 1 }}>
                            {tableLoading ? (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94a3b8", fontSize: 13 }}>
                                    Loading records…
                                </div>
                            ) : (
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                    <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                                        <tr style={{ background: "#f8fafc" }}>
                                            {cols.map(c => (
                                                <th key={c.key} style={{
                                                    padding: "10px 14px", textAlign: "left",
                                                    fontSize: 10, fontWeight: 700,
                                                    color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em",
                                                    borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap",
                                                }}>{c.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {records.length === 0 ? (
                                            <tr>
                                                <td colSpan={cols.length} style={{
                                                    textAlign: "center", padding: "56px 20px",
                                                    color: "#94a3b8", fontSize: 13,
                                                }}>
                                                    No records yet.&nbsp;
                                                    <span
                                                        onClick={openAddForm}
                                                        style={{ color: "#1e40af", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                                                    >+ Add Entry</span>
                                                    &nbsp;to get started.
                                                </td>
                                            </tr>
                                        ) : records.map((row) => {
                                            const dur = calcDuration(row.check_in, row.check_out);
                                            return (
                                                <tr key={row.slNo} className="dr-row" style={{ borderBottom: "1px solid #f1f5f9" }}>
                                                    <td style={{ padding: "9px 14px", color: "#94a3b8", fontWeight: 600 }}>{row.slNo}</td>
                                                    <td style={{ padding: "9px 14px", color: "#475569", whiteSpace: "nowrap" }}>{row.routine_date || "—"}</td>
                                                    <td style={{ padding: "9px 14px" }}>{row.visit_type ? <Badge text={row.visit_type} scheme="blue" /> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                                                    <td style={{ padding: "9px 14px" }}>{row.client_type ? <Badge text={row.client_type} scheme="purple" /> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                                                    <td style={{ padding: "9px 14px", color: "#475569", whiteSpace: "nowrap" }}>{row.visit_purpose || "—"}</td>
                                                    <td style={{ padding: "9px 14px" }}>{row.product ? <Badge text={row.product} scheme="green" /> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                                                    <td style={{ padding: "9px 14px", color: "#1e293b", fontWeight: 600, whiteSpace: "nowrap" }}>{row.customer_name || "—"}</td>
                                                    <td style={{ padding: "9px 14px", color: "#475569", whiteSpace: "nowrap" }}>{row.contact_person || "—"}</td>
                                                    <td style={{ padding: "9px 14px", color: "#475569" }}>{row.contact_number || "—"}</td>
                                                    <td style={{ padding: "9px 14px", color: "#475569", fontSize: 11, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {row.location_name || row.gps_location || "—"}
                                                    </td>
                                                    <td style={{ padding: "9px 14px", color: "#475569" }}>{row.tally_serial_number || "—"}</td>
                                                    <td style={{ padding: "9px 14px" }}>{dur ? <Badge text={dur} scheme="amber" /> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                                                    <td style={{ padding: "9px 14px", color: "#475569", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.remarks || "—"}</td>
                                                    <td style={{ padding: "9px 14px", color: "#475569", whiteSpace: "nowrap" }}>{row.referred_by_contact_person || "—"}</td>
                                                    <td style={{ padding: "9px 14px", color: "#475569", whiteSpace: "nowrap" }}>
                                                        {row.travel_from && row.travel_to ? `${row.travel_from} → ${row.travel_to}` : "—"}
                                                    </td>
                                                    <td style={{ padding: "9px 14px", color: "#475569", whiteSpace: "nowrap" }}>{row.mode_of_transpotation || "—"}</td>
                                                    <td style={{
                                                        padding: "9px 14px",
                                                        color: "#475569",
                                                        whiteSpace: "nowrap"
                                                    }}>
                                                        {row.killometer
                                                            ? `${parseFloat(row.killometer).toFixed(2)} KM`
                                                            : "—"}
                                                    </td>
                                                    <td style={{ padding: "9px 14px", fontWeight: 700, color: "#15803d", whiteSpace: "nowrap" }}>
                                                        {row.expense_amount ? `₹${parseFloat(row.expense_amount).toFixed(2)}` : "—"}
                                                    </td>
                                                    <td style={{ padding: "9px 14px" }}>
                                                        <button
                                                            className="dr-btn-edit"
                                                            onClick={() => openEditForm(row)}
                                                            style={{
                                                                padding: "4px 12px", borderRadius: 6,
                                                                border: "1px solid #e2e8f0", background: "#f8fafc",
                                                                color: "#475569", fontSize: 11, fontWeight: 600,
                                                                cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s",
                                                            }}
                                                        >✏ Edit</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {/* ─── FORM MODAL ───────────────────────────────────────────────── */}
                {showForm && (
                    <div style={{
                        background: "#fff",
                        borderRadius: 16,
                        width: "100%", maxWidth: 860,
                        maxHeight: "92vh",
                        display: "flex", flexDirection: "column",
                        boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
                        border: "1px solid #e2e8f0",
                        animation: "drSlideUp 0.22s ease",
                    }}>

                        {/* Form Header */}
                        <div style={{
                            padding: "15px 20px",
                            borderBottom: "1px solid #f1f5f9",
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            flexShrink: 0,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{ width: 4, height: 22, borderRadius: 4, background: "linear-gradient(180deg,#1e40af,#0369a1)" }} />
                                <div>
                                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
                                        {editRecord ? "Update Visit Entry" : "New Visit Entry"}
                                    </h3>
                                    <p style={{ margin: "1px 0 0", fontSize: 11, color: "#94a3b8" }}>
                                        Fields marked <span style={{ color: "#ef4444" }}>*</span> are required
                                        &nbsp;·&nbsp;Employee:
                                        <span style={{ color: "#1e40af", fontWeight: 700 }}>
                                            {employeeName} ({employeeId})
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowForm(false)}
                                style={{
                                    width: 32, height: 32, borderRadius: 8,
                                    border: "1px solid #e2e8f0", background: "#f8fafc",
                                    cursor: "pointer", display: "flex", alignItems: "center",
                                    justifyContent: "center", color: "#64748b", fontSize: 16,
                                }}
                            >✕</button>
                        </div>

                        {/* Form Body */}
                        <div className="dr-scrollbar" style={{ overflowY: "auto", flex: 1, padding: "18px 20px" }}>

                            {/* Check In / Out panel — GPS auto-fetched on Check In */}
                            <div style={{
                                background: "#f8fafc", border: "1px solid #e2e8f0",
                                borderRadius: 12, padding: "14px 16px", marginBottom: 18,
                            }}>
                                <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                    📍 Location &amp; Time Tracking
                                </p>
                                <p style={{ margin: "0 0 12px", fontSize: 11, color: "#94a3b8" }}>
                                    Location is captured automatically when you press <strong>Check In</strong>. Both Check In and Check Out can only be pressed once.
                                </p>

                                {/* Location name display (read-only) */}
                                {form.location_name && (
                                    <div style={{
                                        display: "flex", alignItems: "center", gap: 8,
                                        background: "#eff6ff", border: "1px solid #bfdbfe",
                                        borderRadius: 8, padding: "8px 14px", marginBottom: 12,
                                    }}>
                                        <span style={{ fontSize: 14 }}>📍</span>
                                        <span style={{ fontSize: 12, color: "#1e40af", fontWeight: 600 }}>{form.location_name}</span>
                                    </div>
                                )}

                                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>

                                    {/* Check In */}
                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>
                                            Check In <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                            {form.check_in ? (
                                                <div style={{
                                                    padding: "7px 14px", borderRadius: 8,
                                                    background: "#f0fdf4", border: "1px solid #bbf7d0",
                                                    color: "#15803d", fontWeight: 700, fontSize: 13,
                                                    minWidth: 70, textAlign: "center",
                                                }}>
                                                    ✓ {form.check_in}
                                                </div>
                                            ) : (
                                                <button
                                                    className="dr-checkin-btn"
                                                    onClick={handleCheckIn}
                                                    disabled={checkInLoading}
                                                    style={{
                                                        padding: "7px 18px", borderRadius: 8, border: "none",
                                                        background: checkInLoading
                                                            ? "#94a3b8"
                                                            : "linear-gradient(90deg,#059669,#10b981)",
                                                        color: "#fff", fontSize: 12, fontWeight: 600,
                                                        cursor: checkInLoading ? "not-allowed" : "pointer",
                                                        whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6,
                                                    }}
                                                >
                                                    {checkInLoading ? (
                                                        <>
                                                            <span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                                                            Locating…
                                                        </>
                                                    ) : "↓ Check In"}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Check Out */}
                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>Check Out</label>
                                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                            {form.check_out ? (
                                                <div style={{
                                                    padding: "7px 14px", borderRadius: 8,
                                                    background: "#fff1f2", border: "1px solid #fecdd3",
                                                    color: "#dc2626", fontWeight: 700, fontSize: 13,
                                                    minWidth: 70, textAlign: "center",
                                                }}>
                                                    ✓ {form.check_out}
                                                </div>
                                            ) : (
                                                <button
                                                    className="dr-checkin-btn"
                                                    onClick={handleCheckOut}
                                                    disabled={!form.check_in}
                                                    style={{
                                                        padding: "7px 18px", borderRadius: 8, border: "none",
                                                        background: !form.check_in
                                                            ? "#e2e8f0"
                                                            : "linear-gradient(90deg,#dc2626,#ef4444)",
                                                        color: !form.check_in ? "#94a3b8" : "#fff",
                                                        fontSize: 12, fontWeight: 600,
                                                        cursor: !form.check_in ? "not-allowed" : "pointer",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >↑ Check Out</button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Duration badge */}
                                    {duration && (
                                        <div style={{
                                            display: "flex", alignItems: "center", gap: 6,
                                            background: "#fefce8", border: "1px solid #fde68a",
                                            borderRadius: 8, padding: "7px 14px",
                                        }}>
                                            <span style={{ fontSize: 12 }}>⏱</span>
                                            <span style={{ fontSize: 13, fontWeight: 700, color: "#a16207" }}>{duration}</span>
                                        </div>
                                    )}
                                </div>

                                {/* No location warning */}
                                {!form.gps_location && !checkInLoading && (
                                    <div style={{
                                        marginTop: 10, padding: "7px 12px",
                                        background: "#fef9c3", border: "1px solid #fde047",
                                        borderRadius: 7, fontSize: 11, color: "#854d0e", fontWeight: 600,
                                    }}>
                                        ⚠ Location not yet captured. You must Check In to record your location before submitting.
                                    </div>
                                )}
                            </div>

                            {/* Fields Grid */}
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(235px, 1fr))", gap: 14 }}>

                                <FormField label="Visit Type" required>
                                    <SearchableDropdown
                                        options={VISIT_TYPE_OPTIONS}
                                        value={form.visit_type}
                                        onChange={(value) =>
                                            setForm(f => ({
                                                ...f,
                                                visit_type: value
                                            }))
                                        }
                                        placeholder="Select Visit Type"
                                    />
                                </FormField>

                                <FormField label="Client Type" required>
                                    <SearchableDropdown
                                        options={CLIENT_TYPE_OPTIONS}
                                        value={form.client_type}
                                        onChange={(value) =>
                                            setForm(f => ({
                                                ...f,
                                                client_type: value
                                            }))
                                        }
                                        placeholder="Select Client Type"
                                    />
                                </FormField>

                                <FormField label="Product Type">
                                    <SearchableDropdown
                                        options={productOptions}
                                        value={form.product}
                                        onChange={(value) =>
                                            setForm(f => ({
                                                ...f,
                                                product: value
                                            }))
                                        }
                                        placeholder="Select Product"
                                    />
                                </FormField>

                                <FormField label="Purpose of Visit">
                                    <input name="visit_purpose" value={form.visit_purpose} onChange={handleChange}
                                        placeholder="e.g., Sales Demo" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Firm Name" required>
                                    <input name="customer_name" value={form.customer_name} onChange={handleChange}
                                        placeholder="Company / Firm Name" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Address">
                                    <input name="address" value={form.address} onChange={handleChange}
                                        placeholder="Street / Area" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="State">
                                    <input name="state" value={form.state} onChange={handleChange}
                                        placeholder="State" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Pincode">
                                    <input name="pincode" value={form.pincode} onChange={handleChange}
                                        placeholder="6-digit Pincode" type="number" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Contact Person" required>
                                    <input name="contact_person" value={form.contact_person} onChange={handleChange}
                                        placeholder="Contact Person Name" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Contact No.">
                                    <input name="contact_number" value={form.contact_number} onChange={handleChange}
                                        placeholder="10-digit Mobile No." type="number" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Tally Serial No.">
                                    <input name="tally_serial_number" value={form.tally_serial_number} onChange={handleChange}
                                        placeholder="Tally Serial Number" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Remarks">
                                    <input name="remarks" value={form.remarks} onChange={handleChange}
                                        placeholder="Short remarks" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Referred by Contact Person">
                                    <input name="referred_by_contact_person" value={form.referred_by_contact_person} onChange={handleChange}
                                        placeholder="Referrer Name" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Referred Contact No.">
                                    <input name="referred_person_number" value={form.referred_person_number} onChange={handleChange}
                                        placeholder="Referrer Number" type="number" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Travel From">
                                    <input name="travel_from" value={form.travel_from} onChange={handleChange}
                                        placeholder="Starting location" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Travel To">
                                    <input name="travel_to" value={form.travel_to} onChange={handleChange}
                                        placeholder="Destination" className="dr-input" style={inputStyle} />
                                </FormField>

                                <FormField label="Mode of Travel">
                                    <SearchableDropdown
                                        options={TRAVEL_MODE_OPTIONS}
                                        value={form.mode_of_transpotation}
                                        onChange={handleTravelModeChange}
                                        placeholder="Select Travel Mode"
                                    />
                                </FormField>

                                {/* Kilometer field - only for Own Vehicle */}
                                {form.mode_of_transpotation === "Own Vehicle" && (
                                    <FormField label="Kilometers (KM)" required>
                                        <input
                                            name="killometer"
                                            value={form.killometer}
                                            onChange={handleChange}
                                            placeholder="Enter total kilometers"
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            className="dr-input"
                                            style={inputStyle}
                                        />

                                        <small style={{
                                            color: "#64748b",
                                            fontSize: 11,
                                            marginTop: 3
                                        }}>
                                            Travel allowance: ₹3 per KM
                                        </small>
                                    </FormField>
                                )}

                                <FormField label="Expenditure Incurred (₹)">
                                    <input
                                        name="expense_amount"
                                        value={form.expense_amount}
                                        onChange={handleChange}
                                        placeholder="Amount in ₹"
                                        type="number"
                                        step="0.01"
                                        readOnly={form.mode_of_transpotation === "Own Vehicle"}
                                        className="dr-input"
                                        style={{
                                            ...inputStyle,
                                            background: form.mode_of_transpotation === "Own Vehicle"
                                                ? "#f1f5f9"
                                                : "#fff",
                                            cursor: form.mode_of_transpotation === "Own Vehicle"
                                                ? "not-allowed"
                                                : "text"
                                        }}
                                    />

                                    {form.mode_of_transpotation === "Own Vehicle" && (
                                        <small style={{
                                            color: "#15803d",
                                            fontSize: 11,
                                            marginTop: 3,
                                            fontWeight: 600
                                        }}>
                                            Automatically calculated: {form.killometer || 0} × ₹3
                                        </small>
                                    )}
                                </FormField>
                            </div>
                        </div>

                        {/* Form Footer */}
                        <div style={{
                            padding: "13px 20px",
                            borderTop: "1px solid #f1f5f9",
                            display: "flex", justifyContent: "flex-end", gap: 10,
                            flexShrink: 0,
                        }}>
                            <button
                                onClick={() => setShowForm(false)}
                                style={{
                                    padding: "8px 18px", borderRadius: 8,
                                    border: "1px solid #e2e8f0", background: "#f8fafc",
                                    color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer",
                                    fontFamily: "Inter, sans-serif",
                                }}
                            >Cancel</button>
                            <button
                                onClick={handleSubmit}
                                disabled={loading || !form.gps_location}
                                title={!form.gps_location ? "Check In first to capture location" : ""}
                                style={{
                                    padding: "8px 22px", borderRadius: 8, border: "none",
                                    background: (loading || !form.gps_location) ? "#94a3b8" : "linear-gradient(90deg,#1e40af,#0369a1)",
                                    color: "#fff", fontSize: 13, fontWeight: 600,
                                    cursor: (loading || !form.gps_location) ? "not-allowed" : "pointer",
                                    boxShadow: (loading || !form.gps_location) ? "none" : "0 2px 8px rgba(30,64,175,0.22)",
                                    fontFamily: "Inter, sans-serif",
                                    opacity: !form.gps_location ? 0.7 : 1,
                                }}
                            >
                                {loading ? "Saving…" : editRecord ? "Update Record" : "Submit Entry"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
    );
}